// components/SEO.jsx
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { getSEOByPage, getSEOByPath, defaultSEO } from "../../config/seoConfig";

/**
 * SEO 组件 - 管理页面的 meta 标签
 *
 * @param {string} pageName - 页面名称（对应 seoConfig 的 key）
 * @param {string} title - 自定义标题（可选，覆盖默认）
 * @param {string} description - 自定义描述（可选）
 * @param {string} keywords - 自定义关键词（可选）
 * @param {string} image - 自定义 OG 图片（可选）
 * @param {boolean} noIndex - 是否禁止索引（可选）
 * @param {ReactNode} children - 额外的 meta 标签（可选）
 */
const SEO = ({
  pageName,
  title,
  description,
  keywords,
  image,
  noIndex,
  children,
}) => {
  const location = useLocation();

  // 优先使用 pageName，否则根据当前路径自动匹配
  const baseSEO = pageName
    ? getSEOByPage(pageName)
    : getSEOByPath(location.pathname);

  // 合并自定义配置
  const seo = {
    title: title || baseSEO.title,
    description: description || baseSEO.description,
    keywords: keywords || baseSEO.keywords,
    canonicalUrl: baseSEO.canonicalUrl,
    ogImage: image || baseSEO.ogImage,
    siteName: baseSEO.siteName,
    locale: baseSEO.locale,
    noIndex: noIndex ?? baseSEO.noIndex,
  };

  return (
    <Helmet>
      {/* 基础 Meta */}
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="keywords" content={seo.keywords} />
      <link rel="canonical" href={seo.canonicalUrl} />

      {/* Robots 控制 */}
      <meta
        name="robots"
        content={
          seo.noIndex
            ? "noindex, nofollow"
            : "index, follow, max-image-preview:large"
        }
      />

      {/* Open Graph - 社交分享 */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:url" content={seo.canonicalUrl} />
      <meta property="og:image" content={seo.ogImage} />
      <meta property="og:site_name" content={seo.siteName} />
      <meta property="og:locale" content={seo.locale} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.ogImage} />

      {/* 移动端优化 */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />

      {/* 额外的自定义 meta 标签 */}
      {children}
    </Helmet>
  );
};

export default SEO;
